import { useId, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { LineColumnDef } from '@project/contracts'
import { ZERO_MONEY, addMoney, addQty, lineAmount, money, pct, qty, rate, subQty, type Money as MoneyValue } from '@project/contracts'
import { Button, Lookup, Money, Select, Table, type ColumnDef as TableColumn } from '../design/index.js'
import { useApi, type LineRow } from '../app/api.js'
import { asDecimalString, asText, formatDate, formatQuantity, formatRate } from './format.js'

/** A row of another grid, offered by a picker column. */
export interface PickableRow {
  readonly value: string
  readonly label: string
}

export interface LineGridProps {
  readonly columns: readonly LineColumnDef[]
  readonly lines: readonly LineRow[]
  readonly readOnly: boolean
  readonly onChange: (lines: readonly LineRow[]) => void
  /**
   * Which storage section a row added here belongs to.
   *
   * Set on every new row, because the server files a row with no section under
   * the document's FIRST grid — so without this an additional charge is saved
   * as an item and counted twice.
   */
  readonly section?: string
  /** Rows a `parentPicker` column may choose from, by that column's key. */
  readonly pickable?: Readonly<Record<string, readonly PickableRow[]>>
  /**
   * What the button that appends a row says, from the grid's own definition —
   * "Add a BOQ line", "Add an estimation line", "Add a charge". Every grid
   * declares one; a screen that says "Add a line" on all of them makes the
   * operator work out which of the four grids they are looking at.
   */
  readonly addLabel?: string
}

/**
 * qty x rate less discount, so the operator sees the effect of a keystroke
 * immediately. It is a preview only: the server recalculates on save and the
 * saved response overwrites it, because the server is the authority on money.
 */
const previewAmount = (row: LineRow): string | null => {
  // An adjustment is typed as its amount: one of it, at that figure.
  const q = asDecimalString(row['quantity']) ?? (row['section'] === 'ADJUSTMENT' ? '1' : null)
  const r = asDecimalString(row['rate'])
  if (q === null || r === null) return asDecimalString(row['amount'])
  const d = asDecimalString(row['discountPct']) ?? '0'
  try {
    return lineAmount(qty(q), rate(r), pct(d))
  } catch {
    return asDecimalString(row['amount'])
  }
}

const gridTotal = (lines: readonly LineRow[]): string => {
  const amounts: MoneyValue[] = []
  for (const line of lines) {
    const amount = previewAmount(line)
    if (amount === null) continue
    try {
      // An addition among deductions counts the other way, as it does on the
      // server, so the grid's own total is the net effect on the document.
      const sign = line['section'] === 'ADJUSTMENT' && String(line['direction'] ?? '').toUpperCase() !== 'ADDITION' ? -1 : 1
      amounts.push(money(sign === -1 ? `-${amount}`.replace('--', '') : amount))
    } catch {
      // A half-typed rate is not an arithmetic error; it simply does not count yet.
    }
  }
  return amounts.length === 0 ? ZERO_MONEY : addMoney(...amounts)
}

const isNumeric = (column: LineColumnDef): boolean =>
  column.type === 'qty' || column.type === 'rate' || column.type === 'money'

/**
 * A formula cell's value, from the row's own figures; null while an operand
 * is still missing or half-typed.
 */
const formulaValue = (formula: NonNullable<LineColumnDef['formula']>, row: LineRow): string | null => {
  try {
    if ('subtract' in formula) {
      const [from, less] = formula.subtract
      const a = asText(row[from])
      if (a === '') return null
      const result = subQty(qty(a), qty(asText(row[less]) || '0'))
      // Floored only where it has to be: a shortfall on a count is negative.
      return formula.floor !== undefined && Number(result) < Number(formula.floor) ? qty(formula.floor) : result
    }
    const parts = formula.sum.map((k) => asText(row[k])).filter((v) => v !== '')
    return parts.length === 0 ? null : addQty(...parts.map((v) => qty(v)))
  } catch {
    // Half-typed; it will count once it is a number.
    return null
  }
}

const staticValue = (column: LineColumnDef, value: unknown) => {
  // A fixed choice reads as its label, not its stored code.
  if (column.options) {
    const option = column.options.find((o) => o.value === asText(value))
    if (option) return option.label
  }
  if (column.type === 'qty') return <span className="num">{formatQuantity(value)}</span>
  if (column.type === 'date') return asText(value) === '' ? null : formatDate(asText(value))
  if (column.type === 'rate') return <span className="num">{formatRate(value)}</span>
  if (column.type === 'money') {
    const decimal = asDecimalString(value)
    return decimal === null ? null : <Money value={decimal} />
  }
  return asText(value)
}

export const LineGrid = ({
  columns, lines, readOnly, onChange, addLabel, section, pickable,
}: LineGridProps) => {
  const patch = (index: number, key: string, value: string) => patchMany(index, { [key]: value })

  /**
   * Several cells of one row in one change. Two `patch` calls in a row both
   * start from the same `lines`, so the second silently undid the first —
   * which is how picking a material could keep its name and lose its id.
   */
  const patchMany = (index: number, values: Readonly<Record<string, string>>) => {
    onChange(lines.map((line, i) => (i === index ? withFormulas({ ...line, ...values }, Object.keys(values)) : line)))
  }

  /**
   * Cells that are arithmetic on two others, kept in step as either is typed
   * — a receipt's accepted quantity is delivered less rejected. The server
   * works it out again on save; this keeps the row and its amount honest
   * until then.
   */
  const withFormulas = (row: LineRow, changed: readonly string[]): LineRow => {
    let next = row
    for (const column of columns) {
      const formula = column.formula
      if (!formula) continue
      try {
        const operands = 'subtract' in formula ? formula.subtract : formula.sum
        if (!operands.some((k) => changed.includes(k))) continue
        const value = formulaValue(formula, next)
        if (value !== null) next = { ...next, [column.key]: value }
      } catch {
        // Half-typed; it will count once it is a number.
      }
    }
    return next
  }

  // The Units master, for a unit cell someone types into. Only fetched when a
  // grid actually lets a unit be typed.
  const api = useApi()
  const unitsListId = useId()
  const unitEditable = !readOnly && columns.some((c) => c.key === 'unit' && c.editable === true)
  const units = useQuery({
    queryKey: ['units', api.companyId],
    queryFn: () => api.masterList('unit', { pageSize: '200' }),
    enabled: unitEditable,
    staleTime: 5 * 60_000,
  })

  const remove = (index: number) => {
    onChange(lines.filter((_, i) => i !== index).map((line, i) => ({ ...line, seq: i + 1 })))
  }

  const add = () => onChange([
    ...lines,
    {
      seq: lines.length + 1,
      quantity: '',
      rate: '',
      description: '',
      ...(section ? { section } : {}),
    },
  ])

  const tableColumns = useMemo<TableColumn<LineRow>[]>(() => {
    // A secondary column is printed under the cell before it, not in its own.
    const groups: { column: LineColumnDef; secondaries: LineColumnDef[] }[] = []
    for (const column of columns) {
      const previous = groups[groups.length - 1]
      if (column.secondary && previous) previous.secondaries.push(column)
      else groups.push({ column, secondaries: [] })
    }

    const built: TableColumn<LineRow>[] = groups.map(({ column, secondaries }) => {
      const spec: TableColumn<LineRow> = { key: column.key, header: column.label }
      if (column.width !== undefined) spec.width = column.width
      if (column.align !== undefined) spec.align = column.align
      else if (isNumeric(column)) spec.align = 'right'

      spec.render = (row) => {
        const index = lines.indexOf(row)
        const main =
          column.key === 'seq' ? (
            <span className="num">{index + 1}</span>
          ) : column.key === 'amount' ? (
            (() => {
              const amount = previewAmount(row)
              return amount === null ? null : <Money value={amount} />
            })()
          ) : column.key === 'itemId' && (readOnly || column.editable !== true) ? (
            // An id is never shown to a person. The name comes from the pull
            // or from the server's own resolution; the id is the last resort.
            <span>{asText(row['itemLabel']) || asText(row['itemName']) || asText(row['itemId'])}</span>
          ) : readOnly || column.editable !== true ? (
            // A looked-up id reads as what it is called, never as the id; a
            // formula cell a picker filled in without it reads as its sum.
            column.lookup && asText(row[`${column.key}Label`]) !== ''
              ? <span>{asText(row[`${column.key}Label`])}</span>
              : staticValue(column, asText(row[column.key]) === '' && column.formula
                ? formulaValue(column.formula, row) : row[column.key])
          ) : column.key === 'itemId' ? (
            // An id column is never typed by hand. Before this, adding a line
            // meant pasting a uuid into a text box.
            <Lookup
              entity={column.lookup?.entity ?? 'item'}
              value={asText(row['itemId'])}
              label={asText(row['itemLabel']) || undefined}
              placeholder={`Search ${column.lookup?.entity === 'charge' ? 'charges' : 'materials'}`}
              onChange={(value, option) => {
                const next: Record<string, string> = { itemId: value }
                if (option) {
                  next['itemLabel'] = option.label
                  // What the material master says, unless the line already
                  // says something else: a unit and an HSN code typed by hand
                  // are the operator's to keep.
                  const unit = option.extra?.['unit']
                  const hsn = option.extra?.['hsnSacCode']
                  if (unit && !asText(row['unit'])) next['unit'] = unit
                  if (hsn && !asText(row['hsnSacCode'])) next['hsnSacCode'] = hsn
                }
                patchMany(index, next)
              }}
            />
          ) : column.key === 'unit' ? (
            <input
              className="line-input"
              aria-label={column.label}
              list={unitsListId}
              value={asText(row['unit'])}
              onChange={(e) => patch(index, 'unit', e.currentTarget.value)}
            />
          ) : column.parentPicker ? (
            // A row of another grid on this document: the BOQ line an estimate
            // costs, the billing term a BOQ line is billed under.
            <Select
              value={asText(row[column.key])}
              onChange={(value) => patch(index, column.key, value)}
              placeholder="Not chosen"
              options={[...(pickable?.[column.key] ?? [])]}
            />
          ) : column.options ? (
            // A fixed-choice cell. Typing MATERIAL by hand into a text box is
            // how one cost type ends up spelt four different ways.
            <Select
              value={asText(row[column.key])}
              onChange={(value) => patch(index, column.key, value)}
              placeholder="Not set"
              options={[...column.options]}
            />
          ) : column.lookup ? (
            <Lookup
              entity={column.lookup.entity}
              value={asText(row[column.key])}
              label={asText(row[`${column.key}Label`]) || undefined}
              placeholder={`Search ${column.label.toLowerCase()}`}
              onChange={(value, option) => {
                const next: Record<string, string> = { [column.key]: value }
                if (option) {
                  next[`${column.key}Label`] = option.label
                  for (const [from, to] of Object.entries(column.lookup?.fills ?? {})) {
                    const carried = option.extra?.[from]
                    if (carried && !asText(row[to])) next[to] = carried
                  }
                }
                patchMany(index, next)
              }}
            />
          ) : (
            <input
              className={isNumeric(column) ? 'line-input line-input--right num' : 'line-input'}
              inputMode={isNumeric(column) ? 'decimal' : undefined}
              // A need-by date, an incurred-on date: the browser's own picker.
              type={column.type === 'date' ? 'date' : undefined}
              aria-label={column.label}
              title={column.hint}
              value={asText(row[column.key])}
              onChange={(e) => patch(index, column.key, e.currentTarget.value)}
            />
          )

        return (
          <>
            {main}
            {secondaries.map((s) =>
              readOnly || s.editable !== true ? (
                <span className="cell-secondary" key={s.key}>{asText(row[s.key])}</span>
              ) : (
                <input
                  key={s.key}
                  className="line-input line-input--secondary"
                  aria-label={s.label}
                  placeholder={s.label}
                  value={asText(row[s.key])}
                  onChange={(e) => patch(index, s.key, e.currentTarget.value)}
                />
              ),
            )}
          </>
        )
      }
      return spec
    })

    if (!readOnly) {
      built.push({
        key: '__remove',
        header: 'Remove',
        width: 110,
        render: (row) => (
          <Button kind="ghost" onClick={() => remove(lines.indexOf(row))}>Remove</Button>
        ),
      })
    }
    return built
  }, [columns, lines, readOnly, unitsListId])

  return (
    <>
      {unitEditable ? (
        <datalist id={unitsListId}>
          {(units.data?.rows ?? []).map((u) => (
            <option key={String(u['id'])} value={asText(u['code'])}>{asText(u['name'])}</option>
          ))}
        </datalist>
      ) : null}
      <Table
        columns={tableColumns}
        rows={lines}
        getKey={(row) => asText(row['id']) || `line-${lines.indexOf(row)}`}
        empty="No lines yet."
        stacked
      />
      <div className="lines-total">
        <span className="label">Lines total</span>
        <Money value={gridTotal(lines)} size={17} />
      </div>
      {readOnly ? null : (
        <div className="row" style={{ marginTop: 16 }}>
          <Button kind="ghost" onClick={add}>{addLabel ?? 'Add a line'}</Button>
        </div>
      )}
    </>
  )
}
